/**
 * EngagementLetterTemplateSelector Component
 */
import React, { useCallback, useEffect, useState } from "react";
import styles from "./EngagementLetterTemplateSelector.module.css";
import { Row } from "react-bootstrap";
import { ThreeCircles } from "react-loader-spinner";
import {
  createTemplateOptions,
  EngagementLetterTemplateSelectorProps,
  TemplateOption,
} from "./EngagementLetterTemplateSelector.utils";
import * as Icon from "react-bootstrap-icons";
import { fetchEngagementTemplateData } from "./EngagementLetterTemplateSelector.service";

/**
 * EngagementLetterTemplateSelector Component
 *
 * Renders the template selection step of the Engagement Letter Wizard.
 * - Fetches and displays available templates.
 * - Handles selection and syncs selected template to shared wizard state.
 * - Supports caching via sharedData.templatesFetched.
 */
const EngagementLetterTemplateSelector: React.FC<
  EngagementLetterTemplateSelectorProps
> = ({ sharedData, setSharedData, onValidationChange }) => {
  const [templateOptions, setTemplateOptions] = useState<TemplateOption[]>([]);
  const [templateAPIProgress, setTemplatesAPIProgress] =
    useState<boolean>(false);

  /**
   * Updating the checked flag of selected option and the templateId in shared data
   */
  const handleTemplateChange = useCallback(
    (templateId: number) => {
      setSharedData((prev) => ({ ...prev, templateId: templateId }));
      setTemplateOptions((prevList) =>
        prevList.map((item) => ({
          ...item,
          checked: item.value === templateId,
        }))
      );
      // Complete the validation process after the template has been selected
      onValidationChange(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSharedData, setTemplateOptions]
  );

  /**
   * Caching the template options data for the page
   */
  useEffect(() => {
    if (sharedData.templatesFetched || sharedData.templateOptions.length) {
      setTemplateOptions(sharedData.templateOptions || []);
      setSharedData((prev) => ({
          ...prev,
          templateOptions: sharedData.templateOptions,
          templatesFetched: true,
        }));
      return;
    }
    const fetchTemplates = async () => {
      try {
        setTemplatesAPIProgress(true);
        const allTemplates = await fetchEngagementTemplateData();
        setTemplatesAPIProgress(false);
        
        const template_options = createTemplateOptions(allTemplates);

        // Set both local and shared data
        setTemplateOptions(template_options);
        setSharedData((prev) => ({
          ...prev,
          templateOptions: template_options,
          templatesFetched: true,
        }));
      } catch (error) {
        console.log("Error occurred while loading your templates.");
      }
    };

    fetchTemplates();
  }, [sharedData.templateOptions, sharedData.templatesFetched]);

  /**
   * Handling the vcase where the template is pre-selected
   * (this keeps the state updated when moving back and forth in the wizard)
   */
  useEffect(() => {
    if (sharedData.templateId) {
      handleTemplateChange(sharedData.templateId);
    }
  }, [sharedData.templateId, handleTemplateChange]);

  return (
    <Row className={styles.templateSelectorContainer}>
      <div className="flex-column-center-center">
        <h6>Template Select</h6>
        <p>Select an appropriate template for your engagement</p>
      </div>

      {templateAPIProgress ? (
        <div className="flex-row-center-center" style={{ marginTop: "20%" }}>
          <div
            className="spinner flex-row-center-center"
            style={{ fontSize: 14, fontWeight: 500 }}
          >
            <ThreeCircles
              height="30"
              width="30"
              color="#4fa94d"
              wrapperStyle={{}}
              wrapperClass=""
              visible={true}
              ariaLabel="three-circles-rotating"
              outerCircleColor="var(--theme1_primary)"
              innerCircleColor="var(--theme1_secondary)"
              middleCircleColor="var(--theme1_tertiary)"
            />
            <p style={{ padding: 0, margin: "0px 0px 0px 10px" }}>
              Loading your templates
            </p>
          </div>
        </div>
      ) : templateOptions.length > 0 ? (
        templateOptions.map((template, index) => (
          <div
            key={template.value}
            className={
              template.checked
                ? "flex-row-center-start template-wrapper active-template-wrapper"
                : "flex-row-center-start template-wrapper"
            }
            onClick={() => handleTemplateChange(template.value)}
          >
            <input
              type="radio"
              className={`${styles.radioTemplate} form-check-input custom-check-input`}
              checked={!!template.checked}
              readOnly
            />
            <Icon.FileEarmarkFill className={`${styles.templateIcon}`}/>
            {template.label}{" "}
            {template.CreatedBy ? `- ${template.CreatedBy}` : ""}
            {
              !!template.checked && <Icon.CheckCircleFill className={`${styles.selectedTemplateFill}`} /> 
            }
          </div>
        ))
      ) : (
        <h6>No templates found.</h6>
      )}
    </Row>
  );
};

export default EngagementLetterTemplateSelector;
